import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  IconButton,
  Link,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  FilePresent,
  CalendarToday,
  Business,
  Email,
  Phone
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { handlePreviewFile } from 'src/services/common';

interface DownloadDetail {
  vendorName: string;
  vendorEmail: string;
  business: string;
  phone: string;
  downloadedDocument: string;
  documentCaption: string;
  downloadDateTime: string;
  documentUrl?: string;
  vendorBusiness?: string;
  vendorPhone?: string;
  documentName?: string;
  downloadDate?: string;
  childBidDocsID: number;
  bidDocumentID: number;
  bidID: number;
  childBidID: number;
}

interface BidDetails {
  contractNo: string;
  title: string;
  categories: string;
}

interface DownloadDetailsProps {
  bidId: string;
  bidDetails: BidDetails;
  onBack: () => void;
}

const DownloadDetails: React.FC<DownloadDetailsProps> = ({
  bidId,
  bidDetails,
  onBack
}) => {
  const theme = useTheme();
  const [parentDownloads, setParentDownloads] = useState<DownloadDetail[]>([]);
  const [childDownloads, setChildDownloads] = useState<DownloadDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [childRowsPerPage, setChildRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [parentPage, setParentPage] = useState(0);
  const [childPage, setChildPage] = useState(0);

  const fetchDownloadDetails = async (pageNum: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(),
        Size: pageSize.toString()
      });

      const response = await httpRequest(
        'GET',
        `/bid_portal//bids/${bidId}/downloaded_details?${queryParams.toString()}`
      );

      if (response.status === 'success') {
        const transformedDownloads: DownloadDetail[] =
          response.data.Data?.map((item: any) => ({
            vendorName: item.VendorName || item.vendorName || '',
            vendorEmail: item.VendorEmail || item.vendorEmail || '',
            business: item.Business || item.business || '',
            phone: item.Phone || item.phone || '',
            downloadedDocument:
              item.DownloadedDocument || item.downloadedDocument || '',
            documentCaption: item.DocumentCaption || item.documentCaption || '',
            downloadDateTime:
              item.DownloadDateTime || item.downloadDateTime || '',
            documentUrl: item.DocumentUrl || item.documentUrl || '',
            vendorBusiness: item.VendorBusiness || item.vendorBusiness || '',
            vendorPhone: item.VendorPhone || item.vendorPhone || '',
            documentName: item.DocumentName || item.documentName || '',
            downloadDate: item.DownloadDate || item.downloadDate || '',
            childBidDocsID: item?.ChildBidDocsID,
            bidDocumentID: item?.BidDocumentID,
            bidID: item?.BidID,
            childBidID: item?.ChildBidID
          })) || [];

        const newParentDownloads = transformedDownloads.filter(
          (download) => download.bidID > 0 && download.childBidID === 0
        );
        const newChildDownloads = transformedDownloads.filter(
          (download) => download.bidID > 0 && download.childBidID > 0
        );

        setParentDownloads(newParentDownloads);
        setChildDownloads(newChildDownloads);
      } else {
        setError(response.message || 'Failed to fetch download details');
        toast.error(response.message || 'Failed to fetch download details');
      }
    } catch (err) {
      console.error('Error fetching download details:', err);
      setError('Failed to fetch download details. Please try again later.');
      toast.error('Failed to fetch download details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDownloadDetails(0, 100);
  }, [bidId]);

  const handleParentPageChange = (event: unknown, newPage: number) => {
    setParentPage(newPage);
  };

  const handleChildPageChange = (event: unknown, newPage: number) => {
    setChildPage(newPage);
  };

  const handleChildRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChildRowsPerPage(parseInt(event.target.value, 10));
    setChildPage(0);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setParentPage(0);
    setChildPage(0);
  };

  // Format date and time
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  const paginatedChildDownloads = childDownloads.slice(
    childPage * childRowsPerPage,
    (childPage + 1) * childRowsPerPage
  );

  const paginatedParentDownloads = parentDownloads.slice(
    parentPage * rowsPerPage,
    (parentPage + 1) * rowsPerPage
  );

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      {/* Header with back button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          gap: 2
        }}
      >
        <IconButton
          onClick={onBack}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 80,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Download Details
        </Typography>
      </Box>

      {/* Bid Information Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 3
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Contract No.: {bidDetails.contractNo}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Title: {bidDetails.title}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Categories: {bidDetails.categories}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
        >
          {error}
        </Alert>
      )}
      {parentDownloads?.length > 0 && (
        <>
          {' '}
          {/* Downloads Table */}
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
            }}
          >
            <Box
              sx={{
                p: 2.5,
                bgcolor: `linear-gradient(to right, ${alpha(
                  theme.palette.primary.main,
                  0.05
                )}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                Download History
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        py: 2
                      },
                      '& .MuiTableCell-body': {
                        py: 1.8
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor's Name</TableCell>
                        <TableCell>Vendor's Email/Fax</TableCell>
                        <TableCell>Business</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Downloaded Document</TableCell>
                        <TableCell>Document Caption</TableCell>
                        <TableCell>Download Date & Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parentDownloads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2
                              }}
                            >
                              <FilePresent
                                sx={{
                                  fontSize: 40,
                                  color: alpha(
                                    theme.palette.text.secondary,
                                    0.6
                                  )
                                }}
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                No download records found for this bid.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedParentDownloads?.map((download, index) => (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.03)
                              },
                              transition: 'background-color 0.2s ease',
                              '&:nth-of-type(even)': {
                                bgcolor: alpha(
                                  theme.palette.background.default,
                                  0.4
                                )
                              }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Business
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.primary.main
                                  }}
                                />
                                {download.vendorName || '-'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Email
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.info.main
                                  }}
                                />
                                <Link
                                  href={`mailto:${download.vendorEmail}`}
                                  sx={{
                                    color: theme.palette.info.main,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  {download.vendorEmail || '-'}
                                </Link>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={download.vendorBusiness || '-'}
                                size="small"
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.secondary.main,
                                    0.1
                                  ),
                                  color: theme.palette.secondary.main,
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Phone
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.success.main
                                  }}
                                />
                                {download.vendorPhone || '-'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {download.downloadedDocument ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handlePreviewFile(
                                      download.downloadedDocument
                                    )
                                  }
                                  sx={{
                                    color: theme.palette.primary.main,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      )
                                    }
                                  }}
                                >
                                  {download.documentName}
                                </Button>
                              ) : (
                                <Typography variant="body2">
                                  {download.documentName || '-'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {download.documentName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <CalendarToday
                                  sx={{
                                    fontSize: 16,
                                    color: theme.palette.text.secondary
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {formatDateTime(download.downloadDate)}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Box
                  sx={{
                    borderTop: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.5)
                  }}
                >
                  <TablePagination
                    component="div"
                    count={parentDownloads?.length}
                    page={parentPage}
                    onPageChange={handleParentPageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                        {
                          fontWeight: 500
                        },
                      '& .MuiTablePagination-select': {
                        borderRadius: 1.5,
                        '&:focus': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05
                          )
                        }
                      },
                      '& .MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          '&:hover': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            )
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </Card>
        </>
      )}

      {childDownloads?.length > 0 && (
        <>
          {/* Downloads Table */}
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              marginTop: '27px'
            }}
          >
            <Box
              sx={{
                p: 2.5,
                bgcolor: `linear-gradient(to right, ${alpha(
                  theme.palette.primary.main,
                  0.05
                )}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                Addendum Download History
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        py: 2
                      },
                      '& .MuiTableCell-body': {
                        py: 1.8
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor's Name</TableCell>
                        <TableCell>Vendor's Email/Fax</TableCell>
                        <TableCell>Business</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Downloaded Document</TableCell>
                        <TableCell>Document Caption</TableCell>
                        <TableCell>Download Date & Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {childDownloads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2
                              }}
                            >
                              <FilePresent
                                sx={{
                                  fontSize: 40,
                                  color: alpha(
                                    theme.palette.text.secondary,
                                    0.6
                                  )
                                }}
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                No download records found for this bid.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedChildDownloads?.map((download, index) => (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.03)
                              },
                              transition: 'background-color 0.2s ease',
                              '&:nth-of-type(even)': {
                                bgcolor: alpha(
                                  theme.palette.background.default,
                                  0.4
                                )
                              }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Business
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.primary.main
                                  }}
                                />
                                {download.vendorName || '-'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Email
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.info.main
                                  }}
                                />
                                <Link
                                  href={`mailto:${download.vendorEmail}`}
                                  sx={{
                                    color: theme.palette.info.main,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  {download.vendorEmail || '-'}
                                </Link>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={download.vendorBusiness || '-'}
                                size="small"
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.secondary.main,
                                    0.1
                                  ),
                                  color: theme.palette.secondary.main,
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Phone
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.success.main
                                  }}
                                />
                                {download.vendorPhone || '-'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {download.downloadedDocument ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handlePreviewFile(
                                      download.downloadedDocument
                                    )
                                  }
                                  sx={{
                                    color: theme.palette.primary.main,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      )
                                    }
                                  }}
                                >
                                  {download.documentName}
                                </Button>
                              ) : (
                                <Typography variant="body2">
                                  {download.documentName || '-'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {download.documentName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <CalendarToday
                                  sx={{
                                    fontSize: 16,
                                    color: theme.palette.text.secondary
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {formatDateTime(download.downloadDate)}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Box
                  sx={{
                    borderTop: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.5)
                  }}
                >
                  <TablePagination
                    component="div"
                    count={childDownloads?.length}
                    page={childPage}
                    onPageChange={handleChildPageChange}
                    rowsPerPage={childRowsPerPage}
                    onRowsPerPageChange={handleChildRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                        {
                          fontWeight: 500
                        },
                      '& .MuiTablePagination-select': {
                        borderRadius: 1.5,
                        '&:focus': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05
                          )
                        }
                      },
                      '& .MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          '&:hover': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            )
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </Card>
        </>
      )}
    </Box>
  );
};

export default DownloadDetails;
